if (!customElements.get('previous-cc-voyages')) {
  class PreviousCCVoyages extends HTMLElement {
    constructor() {
      super();
      this.cardSwiper = null;
      this.modalSwipers = new Map();
      this.activeModal = null;
      this.lastTrigger = null;

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.initCardSwiper();
      this.bindEvents();
    }

    disconnectedCallback() {
      this.destroy();
    }

    get cardSwiperEl() {
      return this.querySelector('.previous-cc-voyages__card');
    }

    get paginationEl() {
      return this.querySelector(':scope > .previous-cc-voyages__card-header .swiper-pagination');
    }

    get prevButtonEl() {
      return this.querySelector(':scope > .previous-cc-voyages__card-header .swiper-button-prev');
    }

    get nextButtonEl() {
      return this.querySelector(':scope > .previous-cc-voyages__card-header .swiper-button-next');
    }

    getCardSwiperConfig() {
      return {
        slidesPerView: 1,
        spaceBetween: 12,
        grabCursor: true,
        keyboard: { enabled: true },
        pagination: this.paginationEl
          ? { el: this.paginationEl, type: 'fraction' }
          : false,
        navigation:
          this.prevButtonEl && this.nextButtonEl
            ? { prevEl: this.prevButtonEl, nextEl: this.nextButtonEl }
            : false,
        breakpoints: {
          768: {
            slidesPerView: 1.6,
            slidesPerGroup: 1,
            spaceBetween: 20,
            slidesOffsetAfter: 20,
            slidesOffsetBefore: 20,
          },
          1025: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 12,
            slidesOffsetAfter: 0,
            slidesOffsetBefore: 0,
          },
        },
      };
    }

    initCardSwiper() {
      if (!this.cardSwiperEl || typeof Swiper === 'undefined') return;
      this.cardSwiper = new Swiper(this.cardSwiperEl, this.getCardSwiperConfig());
      this.classList.add('is-ready');
    }

    bindEvents() {
      this.querySelectorAll('[data-voyage-modal-trigger]').forEach((btn) => {
        btn.addEventListener('click', this.handleTriggerClick);
      });

      this.querySelectorAll('[data-voyage-modal-close]').forEach((btn) => {
        btn.addEventListener('click', this.handleCloseClick);
      });

      document.addEventListener('keydown', this.handleKeydown);
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      const modalId = trigger.getAttribute('data-voyage-modal-trigger');
      if (!modalId) return;
      const modal = this.querySelector(`#${CSS.escape(modalId)}`);
      if (!modal) return;

      this.lastTrigger = trigger;
      this.openModal(modal);
    }

    handleCloseClick(event) {
      const modal = event.currentTarget.closest('[data-voyage-modal]');
      if (modal) this.closeModal(modal);
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.activeModal) {
        this.closeModal(this.activeModal);
      }
    }

    openModal(modal) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = scrollbarWidth + 'px';
      }

      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('previous-cc-voyages--scroll-lock');
      this.activeModal = modal;

      this.initModalSwiper(modal);
      const existing = this.modalSwipers.get(modal.id);
      if (existing) {
        existing.update();
        const resetIndex = existing.firstRealIndex != null ? existing.firstRealIndex : 0;
        existing.slideTo(resetIndex, 0, false);
        existing.update();
      }

      const closeBtn = modal.querySelector('.previous-cc-voyages__modal-close');
      if (closeBtn) closeBtn.focus();
    }

    closeModal(modal) {
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('previous-cc-voyages--scroll-lock');
      document.body.style.paddingRight = '';
      this.activeModal = null;

      if (this.lastTrigger) {
        this.lastTrigger.focus();
        this.lastTrigger = null;
      }
    }

    initModalSwiper(modal) {
      if (this.modalSwipers.has(modal.id)) return;
      const swiperEl = modal.querySelector('.previous-cc-voyages__modal-swiper');
      if (!swiperEl || typeof Swiper === 'undefined') return;

      const wrapper = swiperEl.querySelector('.swiper-wrapper');
      const realSlides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
      const originalCount = realSlides.length;

      let firstRealIndex = 0;
      let lastRealIndex = originalCount - 1;

      if (originalCount > 1) {
        const leftPeek = realSlides[originalCount - 1].cloneNode(true);
        const rightPeek = realSlides[0].cloneNode(true);
        [leftPeek, rightPeek].forEach((clone) => {
          clone.setAttribute('aria-hidden', 'true');
          clone.classList.add('previous-cc-voyages__modal-image--peek-clone');
        });
        wrapper.insertBefore(leftPeek, wrapper.firstChild);
        wrapper.appendChild(rightPeek);
        firstRealIndex = 1;
        lastRealIndex = originalCount;
      }

      const prevEl = modal.querySelector('.previous-cc-voyages__modal-nav .swiper-button-prev');
      const nextEl = modal.querySelector('.previous-cc-voyages__modal-nav .swiper-button-next');

      const syncBounds = (s) => {
        const atFirst = s.activeIndex <= firstRealIndex;
        const atLast = s.activeIndex >= lastRealIndex;
        s.allowSlidePrev = !atFirst;
        s.allowSlideNext = !atLast;
        if (prevEl) prevEl.classList.toggle('swiper-button-disabled', atFirst);
        if (nextEl) nextEl.classList.toggle('swiper-button-disabled', atLast);
      };

      const swiper = new Swiper(swiperEl, {
        slidesPerView: 1.2,
        centeredSlides: true,
        spaceBetween: 12,
        grabCursor: true,
        loop: false,
        initialSlide: firstRealIndex,
        watchOverflow: true,
        keyboard: { enabled: true },
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        breakpoints: {
          768: {
            slidesPerView: 1.4,
            spaceBetween: 20,
          },
          1025: {
            slidesPerView: 1.4,
            spaceBetween: 12,
          },
        },
        on: {
          init: syncBounds,
          slideChange: syncBounds,
        },
      });

      swiper.firstRealIndex = firstRealIndex;

      if (originalCount <= 1 && prevEl && nextEl) {
        prevEl.style.display = 'none';
        nextEl.style.display = 'none';
      }

      this.modalSwipers.set(modal.id, swiper);
    }

    destroy() {
      if (this.cardSwiper) {
        this.cardSwiper.destroy();
        this.cardSwiper = null;
      }
      this.modalSwipers.forEach((swiper) => swiper.destroy());
      this.modalSwipers.clear();

      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove('previous-cc-voyages--scroll-lock');
    }
  }

  customElements.define('previous-cc-voyages', PreviousCCVoyages);
}
